import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import { UserThreeIcon } from '@repo/ui/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { adminUpdateStaff } from '../../api/staff/admin-update-staff'
import { getStaff } from '../../api/staff/get-staff'
import { checkSsnDataValidity } from '../../api/staff-application/check-ssn-data-validity'
import { ItemNotFound } from '../../components/elements/item-not-found'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import UserNavBar from '../../components/elements/nav-bar'
import StaffForm from '../../components/pages/staff/staff-form/staff-form'
import { Paths } from '../../lib/constants/path'
import { AdminStaffInput } from '../../lib/types/models/staff/admin-staff-input'
import { updateButtonsConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import { mapStaffToInput } from './utils'

const UpdateStaffProfile = () => {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const { navigate, back } = useNavigator()

  if (!id || isNaN(parseInt(id))) return <ItemNotFound title="Identifiant de demande invalide" />

  const { mutateAsync } = useMutation({
    mutationFn: adminUpdateStaff,
    onError: showAxiosError,
    onSuccess() {
      queryClient.invalidateQueries({ exact: true, queryKey: ['staff', id] })
      toast.success("Profil de l'encadrant modifié avec succès")
    }
  })

  const { data: staff, isPending: loadingStaff } = useQuery({
    queryKey: ['staff', id],
    queryFn: () => getStaff(+id),
    gcTime: 0, // do not garbage collect the data
    staleTime: 0, // always consider data stale
    refetchOnMount: 'always' // always refetch on component mount
  })

  const { mutateAsync: checkSsn } = useMutation({
    mutationFn: checkSsnDataValidity,
    onError: showAxiosError
  })

  const handleUpdate = async ({
    diplomas,
    idFile,
    paySlip,
    picture,
    eta,
    criminalRecord,
    socialSecurityNumber,
    applicationId: _applicationId,
    application: _application,
    ...values
  }: AdminStaffInput) => {
    if (!staff) return

    const diplomasCheckFile = diplomas.map((diploma) => {
      const { status, file, ...rest } = diploma
      return { status, file: file ?? null, ...rest }
    })
    const ssnWithoutSpaces = socialSecurityNumber?.replace(/\s/g, '') ?? null
    if (ssnWithoutSpaces) {
      const ssnCheck = await checkSsn({
        ssn: ssnWithoutSpaces,
        birthPlace: values.birthPlace,
        birthDate: values.birthDate,
        gender: values.gender,
        birthDepartment: values.birthDepartment ?? undefined
      })
      if (ssnCheck.valid === false) return
    }
    const input = {
      ...values,
      picture: picture ?? null,
      diplomas: diplomasCheckFile,
      idFile: idFile ?? null,
      socialSecurityNumber: ssnWithoutSpaces,
      criminalRecord: criminalRecord ?? null,
      eta: eta ?? null,
      paySlip: paySlip ?? null
    }
    await mutateAsync({ id: staff.id, ...input })
    queryClient.invalidateQueries({ exact: true, queryKey: ['staff', id] })
  }

  if (loadingStaff) return <LoadingScreen />
  if (!staff) return <ItemNotFound title="Encadrant non trouvé" />

  return (
    <div>
      <UserNavBar title="Profil de l'encadrant" icon={UserThreeIcon} />
      <div className="h-4" />
      <Box>
        <StaffForm
          mode="update"
          staffId={staff.id}
          matricule={staff.matricule}
          account={staff.user}
          onAccountReactivated={() =>
            queryClient.invalidateQueries({ exact: true, queryKey: ['staff', id] })
          }
          buttonsConfig={updateButtonsConfig({
            onCancel: () => back(),
            onUpdate: handleUpdate,
            onUpdateThenCreate: async (values) => {
              await handleUpdate(values)
              navigate(Paths.STAFF_CREATE_PAGE)
            },
            onUpdateThenList: async (values) => {
              await handleUpdate(values)
              back()
            }
          })}
          defaultValues={mapStaffToInput(staff)}
        />
      </Box>
    </div>
  )
}

export default UpdateStaffProfile
